import { IEventForm, IField } from '../constants'
import React, { useState } from 'react'
import { ErrorLabel, SectionTitle } from '../style'
import { Input } from 'iebt-zing-storybook'
import { INPUT_MASK_FORMAT } from 'sharedKernel/constants'
import { buildValidations } from '../behaviors/validation'
import { getAddressByZIP } from 'sharedKernel/api/utils/cep'
import { IViaCepServiceAddressResponse } from 'sharedKernel/api/utils/cep/contract'
import { showToast } from 'components/toast'

interface AddressFormAreaProps {
  addressTab: any
  disabled?: boolean
  visible?: boolean
  updateState?: (item: any) => void
}

export function AddressFormArea({ addressTab, disabled = false, visible = false, updateState }: AddressFormAreaProps): JSX.Element {
  const [item, setItem] = useState(addressTab)

  const getField = (id: string): IField => {
    return item.content[0].content.fields.find((field: IField) => field.id === id) as IField
  }

  const handleInputChange = (value: string, id: string): void => {
    const fields = item.content[0].content.fields
    const fieldIndex = fields.findIndex((field: IField) => field.id === id)
    if (fieldIndex !== null && fieldIndex !== -1) {
      fields[fieldIndex] = { ...fields[fieldIndex], value }
      const newItem = { ...item }
      setItem(newItem)
      if (updateState !== null && updateState !== undefined) updateState(newItem)
    }
  }

  const handleZIPActions = async (zipcode: string): Promise<void> => {
    if (zipcode != null && zipcode !== '') {
      await autoFillAddressByZIP(zipcode.replace(/\D/g, ''))
    }
  }

  const autoFillAddressByZIP = async (zipcode: string): Promise<void> => {
    await getAddressByZIP(zipcode)
      .then((response) => {
        fillAddress(response)
      }).catch((error) => {
        showToast('error', error)
      })
  }

  const fillAddress = (address: IViaCepServiceAddressResponse): void => {
    handleInputChange(address.uf, 'addressState')
    handleInputChange(address.logradouro, 'addressLineOne')
    handleInputChange(address.localidade, 'addressCity')
    handleInputChange(address.bairro, 'addressNeighborhood')
  }

  const fields = {
    name: getField('name'),
    addressZipCode: getField('addressZipCode'),
    addressLineOne: getField('addressLineOne'),
    addressNumber: getField('addressNumber'),
    addressLineTwo: getField('addressLineTwo'),
    addressNeighborhood: getField('addressNeighborhood'),
    addressCity: getField('addressCity'),
    addressState: getField('addressState')
  }

  if (!visible) {
    return <div />
  }

  return (
      <div style={{ margin: '32px 0' }}>
        <SectionTitle>{item.name}</SectionTitle>
          <div style={{ display: 'flex', gap: 16, paddingLeft: 8 }}>
            <div style={{ flex: 1 }}>
              <Input
                id='name'
                label={fields.name.label}
                className={buildValidations(fields.name.validations)}
                value={fields.name.value}
                placeholder={fields.name.namePlaceholder}
                maxLength={fields.name.maxLength}
                isRequired={visible}
                description={fields.name.description}
                onChange={event => handleInputChange(event.target.value, 'name')}
                data-cy={fields.name.dataCy}
                disabled={fields.name.disabled ?? disabled}
              />
              <ErrorLabel className={`errors-${fields.name.id}`}></ErrorLabel>
            </div>
            <div style={{ flex: 1 }}>
              <Input
                id='addressZipCode'
                label={fields.addressZipCode.label}
                code='masked'
                mask={INPUT_MASK_FORMAT.CEP}
                className={buildValidations(fields.addressZipCode.validations)}
                value={fields.addressZipCode.value}
                placeholder={fields.addressZipCode.namePlaceholder}
                maxLength={fields.addressZipCode.maxLength}
                isRequired={visible}
                description={fields.addressZipCode.description}
                onChange={event => {
                  handleInputChange(event.target.value, 'addressZipCode')
                }}
                onBlur={event => {
                  if (event.target.value !== '') {
                    handleZIPActions(event.target.value)
                  }
                }}
                data-cy={fields.addressZipCode.dataCy}
                disabled={fields.addressZipCode.disabled ?? disabled}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 16, paddingLeft: 8 }}>
            <div style={{ flex: 1 }}>
              <Input
                id='addressLineOne'
                label={fields.addressLineOne.label}
                className={buildValidations(fields.addressLineOne.validations)}
                value={fields.addressLineOne.value}
                placeholder={fields.addressLineOne.namePlaceholder}
                maxLength={fields.addressLineOne.maxLength}
                isRequired={visible}
                description={fields.addressLineOne.description}
                onChange={event => handleInputChange(event.target.value, 'addressLineOne')}
                data-cy={fields.addressLineOne.dataCy}
                disabled={fields.addressLineOne.disabled ?? disabled}
              />
              <ErrorLabel className={`errors-${fields.addressLineOne.id}`}></ErrorLabel>
            </div>
            <div style={{ flex: 1 }}>
              <Input
                id='addressNumber'
                label={fields.addressNumber.label}
                className={buildValidations(fields.addressNumber.validations)}
                value={fields.addressNumber.value}
                placeholder={fields.addressNumber.namePlaceholder}
                maxLength={fields.addressNumber.maxLength}
                isRequired={visible}
                description={fields.addressNumber.description}
                onChange={event => handleInputChange(event.target.value, 'addressNumber')}
                data-cy={fields.addressNumber.dataCy}
                disabled={fields.addressNumber.disabled ?? disabled}
              />
              <ErrorLabel className={`errors-${fields.addressNumber.id}`}></ErrorLabel>
            </div>
            <div style={{ flex: 1 }}>
              <Input
                id='addressLineTwo'
                label={fields.addressLineTwo.label}
                className={buildValidations(fields.addressLineTwo.validations)}
                value={fields.addressLineTwo.value}
                placeholder={fields.addressLineTwo.namePlaceholder}
                maxLength={fields.addressLineTwo.maxLength}
                isRequired={fields.addressLineTwo.isRequired}
                description={fields.addressLineTwo.description}
                onChange={event => handleInputChange(event.target.value, 'addressLineTwo')}
                data-cy={fields.addressLineTwo.dataCy}
                disabled={fields.addressLineTwo.disabled ?? disabled}
              />
              <ErrorLabel className={`errors-${fields.addressLineTwo.id}`}></ErrorLabel>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 16, paddingLeft: 8 }}>
            <div style={{ flex: 1 }}>
              <Input
                id='addressNeighborhood'
                label={fields.addressNeighborhood.label}
                className={buildValidations(fields.addressNeighborhood.validations)}
                value={fields.addressNeighborhood.value}
                placeholder={fields.addressNeighborhood.namePlaceholder}
                maxLength={fields.addressNeighborhood.maxLength}
                isRequired={visible}
                description={fields.addressNeighborhood.description}
                onChange={event => handleInputChange(event.target.value, 'addressNeighborhood')}
                data-cy={fields.addressNeighborhood.dataCy}
                disabled={fields.addressNeighborhood.disabled ?? disabled}
              />
              <ErrorLabel className={`errors-${fields.addressNeighborhood.id}`}></ErrorLabel>
            </div>
            <div style={{ flex: 1 }}>
              <Input
                id='addressCity'
                label={fields.addressCity.label}
                className={buildValidations(fields.addressCity.validations)}
                value={fields.addressCity.value}
                placeholder={fields.addressCity.namePlaceholder}
                maxLength={fields.addressCity.maxLength}
                isRequired={visible}
                description={fields.addressCity.description}
                onChange={event => handleInputChange(event.target.value, 'addressCity')}
                data-cy={fields.addressCity.dataCy}
                disabled={fields.addressCity.disabled ?? disabled}
              />
              <ErrorLabel className={`errors-${fields.addressCity.id}`}></ErrorLabel>
            </div>
            <div style={{ flex: 1 }}>
              <Input
                id='addressState'
                label={fields.addressState.label}
                className={buildValidations(fields.addressState.validations)}
                value={fields.addressState.value}
                placeholder={fields.addressState.namePlaceholder}
                maxLength={fields.addressState.maxLength}
                isRequired={visible}
                description={fields.addressState.description}
                onChange={event => handleInputChange(event.target.value, 'addressState')}
                data-cy={fields.addressState.dataCy}
                disabled={fields.addressState.disabled ?? disabled}
              />
              <ErrorLabel className={`errors-${fields.addressState.id}`}></ErrorLabel>
                </div>
            </div>
        </div>
  )
}
